import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface PhoneVerificationProps {
  phoneNumber: string;
  /** Drives the 인증완료 line. Set by 휴대폰 본인확인 (NICE). */
  isVerified: boolean;
  onPhoneChange: (phone: string) => void;
  /** If true, the phone number was loaded from DB and starts read-only. */
  isExisting?: boolean;
  /** If true, hide the 변경 button so the stored number cannot be edited here. */
  hideChangeButton?: boolean;
}

const COUNTRY_CODES = [
  { code: '+82', label: '🇰🇷 +82' },
  { code: '+1', label: '🇺🇸 +1' },
  { code: '+81', label: '🇯🇵 +81' },
  { code: '+86', label: '🇨🇳 +86' },
  { code: '+44', label: '🇬🇧 +44' },
  { code: '+49', label: '🇩🇪 +49' },
  { code: '+33', label: '🇫🇷 +33' },
  { code: '+61', label: '🇦🇺 +61' },
  { code: '+65', label: '🇸🇬 +65' },
  { code: '+84', label: '🇻🇳 +84' },
  { code: '+66', label: '🇹🇭 +66' },
  { code: '+63', label: '🇵🇭 +63' },
  { code: '+62', label: '🇮🇩 +62' },
  { code: '+60', label: '🇲🇾 +60' },
  { code: '+91', label: '🇮🇳 +91' },
];

/** Extract the country code from a full phone number */
function extractCountryCode(phone: string): string {
  for (const { code } of COUNTRY_CODES) {
    if (phone.startsWith(code)) return code;
  }
  return '+82';
}

/** Extract the local number (digits after country code) */
function extractLocalNumber(phone: string, countryCode: string): string {
  return phone.startsWith(countryCode) ? phone.slice(countryCode.length) : phone.replace(/^\+\d+/, '');
}

/**
 * Phone number field plus its verification status.
 *
 * The SMS OTP flow this used to own is gone: 휴대폰 인증 is done by 휴대폰 본인확인
 * (NICE), which proves possession and identity in one pass, and its resolvers
 * were removed along with the sms-verification Lambda. What remains is the
 * number itself — displayed, editable, and marked 인증완료 when NICE has attested
 * it. `isVerified` is therefore reported by the parent, never established here.
 */
const PhoneVerification: React.FC<PhoneVerificationProps> = ({
  phoneNumber,
  isVerified,
  onPhoneChange,
  isExisting = false,
  hideChangeButton = false,
}) => {
  const { t } = useTranslation('profile');

  const [isLocked, setIsLocked] = useState(isExisting && !!phoneNumber.trim());
  const [countryCode, setCountryCode] = useState(() => extractCountryCode(phoneNumber));

  // If phone number already exists, show as read-only with Change button
  if (isLocked) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          {/* 320px matches the name field in PersonalInfoSection so the two rows
              line up; change both together. Capped rather than stretched so the
              button — when shown — sits outside the box instead of squeezing it. */}
          <input
            type="tel"
            value={phoneNumber}
            readOnly
            className="flex-1 max-w-[320px] px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed focus:outline-none"
          />
          {!hideChangeButton && (
            <button
              type="button"
              onClick={() => setIsLocked(false)}
              className="px-3 py-2 text-sm font-medium text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors whitespace-nowrap"
            >
              {t('myProfile.form.phoneChange')}
            </button>
          )}
        </div>
        {/* Below the field, not beside it. As a boxed chip inside the row it
            carried `whitespace-nowrap` and so refused to give up width, while
            the input was `flex-1` — on a narrow screen the input collapsed. A
            plain line underneath cannot compete for horizontal space at any
            width. */}
        {isVerified && (
          <p className="flex items-center gap-1 text-xs font-semibold text-[#127C4A]">
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            {t('myProfile.form.phoneVerified')}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        {/* Country code + number are one visual box: the rounded-l/rounded-r pair
            and the input's absent left border only join up when the two sit
            adjacent, so they are grouped here without a gap. The group is capped
            at 320px to match the name field in PersonalInfoSection — change both
            together. */}
        <div className="flex items-center flex-1 max-w-[320px]">
          <select
            value={countryCode}
            onChange={(e) => {
              const newCode = e.target.value;
              const local = extractLocalNumber(phoneNumber, countryCode);
              setCountryCode(newCode);
              onPhoneChange(newCode + local);
            }}
            className="min-w-[6rem] flex-shrink-0 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-l-lg focus:ring-1 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            {COUNTRY_CODES.map((c) => (
              <option key={c.code} value={c.code}>{c.label}</option>
            ))}
          </select>
          <input
            type="tel"
            value={extractLocalNumber(phoneNumber, countryCode)}
            onChange={(e) => {
              const digits = e.target.value.replace(/\D/g, '');
              onPhoneChange(countryCode + digits);
            }}
            className="flex-1 min-w-0 px-3 py-2 text-sm border-y border-r border-gray-300 rounded-r-lg focus:ring-1 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder={t('myProfile.form.phonePlaceholder')}
          />
        </div>
      </div>

      {isVerified && (
        <p className="flex items-center gap-1 text-xs font-semibold text-[#127C4A]">
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          {t('myProfile.form.phoneVerified')}
        </p>
      )}
    </div>
  );
};

export default PhoneVerification;
